import { useState, useMemo } from "react";
import { useLogoStore, type ColorCategory } from "../state/logoStore";
import { ImageUploader } from "../components/ImageUploader";
import { PageLabel } from "../components/ui/PageLabel";
import { renderLogoSVG } from "../engine/render";
import { suggestSlogans } from "../engine/sloganSuggester";

type Step = 1 | 2 | 3 | 4 | 5;

const INDUSTRIES = [
  { id: "tech",        name: "Technology",       glyph: "💻" },
  { id: "health",      name: "Health & Wellness", glyph: "🏥" },
  { id: "food",        name: "Food & Beverage",  glyph: "🍴" },
  { id: "beauty",      name: "Beauty & Spa",     glyph: "💅" },
  { id: "finance",     name: "Finance",          glyph: "💰" },
  { id: "fitness",     name: "Fitness & Sports", glyph: "💪" },
  { id: "retail",      name: "Retail",           glyph: "🛍️" },
  { id: "real-estate", name: "Real Estate",      glyph: "🏠" },
  { id: "education",   name: "Education",        glyph: "📚" },
  { id: "creative",    name: "Creative & Design",glyph: "🎨" },
  { id: "consulting",  name: "Consulting",       glyph: "💼" },
  { id: "travel",      name: "Travel",           glyph: "🌍" },
  { id: "pets",        name: "Pets",             glyph: "🐾" },
  { id: "photo",       name: "Photography",      glyph: "📷" },
  { id: "auto",        name: "Auto",             glyph: "🚗" },
  { id: "other",       name: "Other",            glyph: "⭐" },
];

// Symbol-type tags per industry — same shape as Looka's "Pick some symbol types" step
const SYMBOL_TYPES_BY_INDUSTRY: Record<string, string[]> = {
  tech:        ["Tech", "Code", "Network", "Cloud", "Speed", "Innovation", "Abstract", "Hex", "Spark", "Future", "Data", "AI"],
  health:      ["Medical", "Heart", "Health", "Healthcare", "Medicine", "Pharmacy", "Hospital", "Wellness", "Dental", "Doctor", "Therapy", "Mental Health", "Vision", "Abstract", "Leaf", "Star", "Shield", "Sustainability", "Innovation", "Strength", "Excellence", "Tranquility", "Creativity"],
  food:        ["Food", "Restaurant", "Cafe", "Bakery", "Organic", "Leaf", "Heart", "Flame", "Coffee", "Wine", "Fresh", "Artisan"],
  beauty:      ["Beauty", "Spa", "Salon", "Elegance", "Heart", "Diamond", "Spark", "Moon", "Floral", "Skincare", "Hair", "Nails"],
  finance:     ["Finance", "Money", "Trust", "Shield", "Diamond", "Hex", "Growth", "Stability", "Investment", "Banking", "Strength", "Excellence"],
  fitness:     ["Fitness", "Strength", "Energy", "Bolt", "Flame", "Star", "Athletic", "Power", "Endurance", "Movement"],
  retail:      ["Retail", "Shopping", "Quality", "Diamond", "Star", "Heart", "Premium", "Curated"],
  "real-estate": ["Property", "Home", "Shield", "Tree", "Hex", "Trust", "Architecture", "Foundation"],
  education:   ["Education", "Learning", "Star", "Tree", "Shield", "Hex", "Knowledge", "Growth", "Inspiration"],
  creative:    ["Creative", "Design", "Spark", "Diamond", "Infinity", "Wave", "Moon", "Art", "Inspiration", "Vision"],
  consulting:  ["Consulting", "Strategy", "Hex", "Shield", "Diamond", "Trust", "Growth", "Excellence", "Insight"],
  travel:      ["Travel", "Globe", "Wave", "Moon", "Drop", "Adventure", "Journey", "Compass"],
  pets:        ["Pets", "Heart", "Drop", "Leaf", "Care", "Friendly", "Joy"],
  photo:       ["Photography", "Spark", "Diamond", "Ring", "Moon", "Lens", "Vision", "Light"],
  auto:        ["Auto", "Bolt", "Shield", "Hex", "Spark", "Speed", "Power", "Performance"],
  other:       ["Abstract", "Heart", "Star", "Shield", "Spark", "Diamond", "Leaf", "Innovation", "Excellence", "Quality"],
};

const COLOR_CATS: Array<{ id: ColorCategory; name: string; gradient: string }> = [
  { id: "blue",      name: "Blue",      gradient: "linear-gradient(135deg, #93c5fd, #2563eb)" },
  { id: "purple",    name: "Purple",    gradient: "linear-gradient(135deg, #c084fc, #6d28d9)" },
  { id: "pink",      name: "Pink",      gradient: "linear-gradient(135deg, #f472b6, #be185d)" },
  { id: "red",       name: "Red",       gradient: "linear-gradient(135deg, #fca5a5, #b91c1c)" },
  { id: "orange",    name: "Orange",    gradient: "linear-gradient(135deg, #fdba74, #c2410c)" },
  { id: "yellow",    name: "Yellow",    gradient: "linear-gradient(135deg, #fde047, #ca8a04)" },
  { id: "green",     name: "Green",     gradient: "linear-gradient(135deg, #86efac, #16a34a)" },
  { id: "teal",      name: "Teal",      gradient: "linear-gradient(135deg, #5eead4, #0f766e)" },
  { id: "greyscale", name: "Greyscale", gradient: "linear-gradient(135deg, #cbd5e1, #334155)" },
];

const INSPIRATIONS = [
  { id: "i1",  iconId: "spark",   layout: "icon-top",  titleFont: "Montserrat",       sloganFont: "Inter",   titleColor: "#0f172a", sloganColor: "#64748b", iconColor: "#3b82f6", background: "#ffffff", title: "LOGOMAN PRO", slogan: "design studio" },
  { id: "i2",  iconId: "leaf",    layout: "icon-left", titleFont: "Playfair Display", sloganFont: "Lora",    titleColor: "#14532d", sloganColor: "#65a30d", iconColor: "#16a34a", background: "#f0fdf4", title: "Greenleaf",  slogan: "organics" },
  { id: "i3",  iconId: "bolt",    layout: "icon-top",  titleFont: "Bebas Neue",       sloganFont: "Inter",   titleColor: "#facc15", sloganColor: "#fafafa", iconColor: "#facc15", background: "#0f172a", title: "VOLT",       slogan: "energy bar" },
  { id: "i4",  iconId: "drop",    layout: "icon-top",  titleFont: "Raleway",          sloganFont: "Raleway", titleColor: "#0c4a6e", sloganColor: "#0284c7", iconColor: "#0ea5e9", background: "#f0f9ff", title: "Hydro",      slogan: "pure water co." },
  { id: "i5",  iconId: "heart",   layout: "icon-left", titleFont: "Pacifico",         sloganFont: "Caveat",  titleColor: "#be185d", sloganColor: "#9333ea", iconColor: "#ec4899", background: "#fdf2f8", title: "Beloved",    slogan: "florist" },
  { id: "i6",  iconId: "shield",  layout: "icon-top",  titleFont: "Oswald",           sloganFont: "Inter",   titleColor: "#1e293b", sloganColor: "#475569", iconColor: "#1e293b", background: "#f1f5f9", title: "GUARDIAN",   slogan: "security" },
  { id: "i7",  iconId: "globe",   layout: "icon-left", titleFont: "Poppins",          sloganFont: "Poppins", titleColor: "#1e3a8a", sloganColor: "#3b82f6", iconColor: "#2563eb", background: "#eff6ff", title: "Worldlink",  slogan: "global tech" },
  { id: "i8",  iconId: "hex",     layout: "icon-top",  titleFont: "JetBrains Mono",   sloganFont: "JetBrains Mono", titleColor: "#0c0a09", sloganColor: "#78716c", iconColor: "#f97316", background: "#fafaf9", title: "hexlab",     slogan: "// dev studio" },
  { id: "i9",  iconId: "infinity",layout: "icon-top",  titleFont: "Anton",            sloganFont: "Inter",   titleColor: "#7c2d12", sloganColor: "#c2410c", iconColor: "#ea580c", background: "#fff7ed", title: "INFINITY",   slogan: "fitness" },
  { id: "i10", iconId: "moon",    layout: "icon-left", titleFont: "Merriweather",     sloganFont: "Lora",    titleColor: "#fafafa", sloganColor: "#a1a1aa", iconColor: "#fde68a", background: "#18181b", title: "Lunar",      slogan: "night cafe" },
  { id: "i11", iconId: "tree",    layout: "icon-top",  titleFont: "Lora",             sloganFont: "Lora",    titleColor: "#365314", sloganColor: "#65a30d", iconColor: "#4d7c0f", background: "#f7fee7", title: "Oakwood",    slogan: "forest goods" },
  { id: "i12", iconId: "diamond", layout: "icon-top",  titleFont: "Playfair Display", sloganFont: "Inter",   titleColor: "#1e1b4b", sloganColor: "#6366f1", iconColor: "#6366f1", background: "#eef2ff", title: "Crown",      slogan: "luxury group" },
];

// === PAGE: ONBOARDING (5 steps: Industry → Symbols → Inspirations → Colors → Name) ===
export function OnboardingPage() {
  const {
    onboarding, setPage, setIndustry, toggleSymbolType, toggleInspiration, toggleColorPreference,
    title, slogan, setTitle, setSlogan,
  } = useLogoStore();
  const [step, setStep] = useState<Step>(1);

  function next() {
    if (step < 5) setStep((step + 1) as Step);
    else { setPage("templates"); window.scrollTo({ top: 0 }); }
  }
  function back() {
    if (step === 1) setPage("start");
    else setStep((step - 1) as Step);
  }
  function skip() {
    if (step < 5) setStep((step + 1) as Step);
    else { setPage("templates"); window.scrollTo({ top: 0 }); }
  }

  const symbolPool = onboarding.industry
    ? (SYMBOL_TYPES_BY_INDUSTRY[onboarding.industry] || SYMBOL_TYPES_BY_INDUSTRY.other)
    : SYMBOL_TYPES_BY_INDUSTRY.other;

  const stepNames = ["Industry", "Symbols", "Inspirations", "Colors", "Name"];
  return (
    <div className="page onboarding">
      <PageLabel name={`ONBOARDING · ${step}/5 ${stepNames[step - 1]}`} />
      <header className="onb-head">
        <button className="onb-back" onClick={back}>&larr; Back</button>
        <div className="onb-progress">
          {[1, 2, 3, 4, 5].map((n) => (
            <span key={n} className={`pdot ${step >= n ? "active" : ""}`} />
          ))}
        </div>
        {step !== 1 ? (
          <button className="btn ghost" onClick={skip}>Skip &rarr;</button>
        ) : <span style={{ width: 60 }} />}
      </header>

      {step === 1 && (
        <section className="onb-section">
          <h1>Pick your industry</h1>
          <p className="onb-sub">Knowing your industry helps us pick symbols, colors, and styles that fit.</p>
          <div className="industry-grid">
            {INDUSTRIES.map((i) => (
              <button
                key={i.id}
                className={`industry-card ${onboarding.industry === i.id ? "active" : ""}`}
                onClick={() => setIndustry(i.id)}
              >
                <span className="industry-glyph">{i.glyph}</span>
                <span className="industry-name">{i.name}</span>
              </button>
            ))}
          </div>
          <div className="onb-cta">
            <button className="btn primary big" disabled={!onboarding.industry} onClick={next}>Continue &rarr;</button>
          </div>
        </section>
      )}

      {step === 2 && (
        <section className="onb-section">
          <h1>Pick some symbol types</h1>
          <p className="onb-sub">We've hand-curated symbols for these types. Pick any that resonate with your brand.</p>
          <div className="chip-grid">
            {symbolPool.map((tag) => (
              <button
                key={tag}
                className={`big-chip ${onboarding.symbolTypes.includes(tag) ? "active" : ""}`}
                onClick={() => toggleSymbolType(tag)}
              >{tag}</button>
            ))}
          </div>
          <div className="onb-bottom-row">
            <button className="link-action" onClick={skip}>I want to pick my own symbols</button>
            <button className="btn primary big" onClick={next}>Continue &rarr;</button>
          </div>
        </section>
      )}

      {step === 3 && (
        <section className="onb-section">
          <h1>Pick some logos you like</h1>
          <p className="onb-sub">We'll use these as inspiration. Tap any to like — pick as many as you want.</p>
          <div className="inspiration-grid">
            {INSPIRATIONS.map((tpl) => {
              const liked = onboarding.likedInspirations.includes(tpl.id);
              const svg = renderLogoSVG(
                {
                  title: tpl.title, slogan: tpl.slogan, iconId: tpl.iconId,
                  layout: tpl.layout as Parameters<typeof renderLogoSVG>[0]["layout"],
                  titleFont: tpl.titleFont, sloganFont: tpl.sloganFont,
                  titleColor: tpl.titleColor, sloganColor: tpl.sloganColor, iconColor: tpl.iconColor,
                  background: tpl.background, titleSize: 56, sloganSize: 18, iconScale: 1,
                },
                { width: 400, height: 240 }
              );
              return (
                <button
                  key={tpl.id}
                  className={`inspiration-card ${liked ? "liked" : ""}`}
                  onClick={() => toggleInspiration(tpl.id)}
                >
                  <div dangerouslySetInnerHTML={{ __html: svg }} />
                  {liked && <div className="like-badge">♥</div>}
                </button>
              );
            })}
          </div>
          <div className="onb-cta">
            <button className="btn primary big" onClick={next}>
              {onboarding.likedInspirations.length > 0
                ? `Continue with ${onboarding.likedInspirations.length} pick${onboarding.likedInspirations.length > 1 ? "s" : ""} →`
                : "Continue →"}
            </button>
          </div>
        </section>
      )}

      {step === 4 && (
        <section className="onb-section">
          <h1>Pick some colors you like</h1>
          <p className="onb-sub">Colors help convey emotion in your logo. Pick categories — or upload an image we'll match.</p>
          <div className="color-cat-grid">
            {COLOR_CATS.map((c) => {
              const active = onboarding.colorPreferences.includes(c.id);
              return (
                <button
                  key={c.id}
                  className={`color-cat ${active ? "active" : ""}`}
                  style={{ backgroundImage: c.gradient }}
                  onClick={() => toggleColorPreference(c.id)}
                >
                  <span>{c.name}</span>
                  {active && <span className="cat-check">✓</span>}
                </button>
              );
            })}
          </div>
          <div className="onb-divider"><span>OR upload an image to extract colors</span></div>
          <div className="onb-uploader">
            <ImageUploader />
          </div>
          <div className="onb-cta">
            <button className="btn primary big" onClick={next}>Continue &rarr;</button>
          </div>
        </section>
      )}

      {step === 5 && (
        <NameStep
          title={title}
          slogan={slogan}
          industry={onboarding.industry}
          setTitle={setTitle}
          setSlogan={setSlogan}
          onContinue={next}
        />
      )}
    </div>
  );
}

function NameStep({
  title, slogan, industry, setTitle, setSlogan, onContinue,
}: {
  title: string; slogan: string; industry: string | null;
  setTitle: (t: string) => void; setSlogan: (s: string) => void; onContinue: () => void;
}) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionSeed, setSuggestionSeed] = useState(0);
  const suggestions = useMemo(
    () => suggestSlogans(industry, title, 6),
    [industry, title, suggestionSeed]
  );

  return (
    <section className="onb-section">
      <div className="name-step-head">
        <div>
          <h1>Enter your company name</h1>
          <p className="onb-sub">You can always change these later.</p>
        </div>
        <button
          className="btn primary big"
          disabled={!title.trim()}
          onClick={onContinue}
        >Continue &rarr;</button>
      </div>

      <div className="name-fields">
        <div className="name-field">
          <label>Company Name</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Your Brand"
            autoFocus
          />
        </div>
        <div className="name-field">
          <div className="name-field-head">
            <label>Add a Slogan (Optional)</label>
            <button
              className="suggest-btn"
              onClick={() => { setShowSuggestions((v) => !v); if (showSuggestions) setSuggestionSeed((v) => v + 1); }}
              title={industry ? `Tailored to your industry: ${industry}` : "Tailored slogans"}
            >
              ✨ Suggest slogans
            </button>
          </div>
          <input
            type="text"
            value={slogan}
            onChange={(e) => setSlogan(e.target.value)}
            placeholder="Add a Slogan (Optional)"
          />

          {showSuggestions && (
            <div className="slogan-suggestions">
              <div className="slogan-suggestions-head">
                <span>Tap any to use{industry ? ` · tailored to ${industry}` : ""}</span>
                <button className="link-btn" onClick={() => setSuggestionSeed((v) => v + 1)}>↻ More</button>
              </div>
              <div className="slogan-list">
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    className="slogan-pill"
                    onClick={() => { setSlogan(s); setShowSuggestions(false); }}
                  >{s}</button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
